import { studentShortLabel } from "@/lib/utils-app";
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import CollapsibleSection from "./CollapsibleSection";
import SignatureMethodSettings from "./SignatureMethodSettings";
import LinkTtlSettings from "./LinkTtlSettings";
import { ContractForm } from "./ContractDialog";
import AutorizacionDialog from "./AutorizacionDialog";
import DgtDocumentsDialog from "./DgtDocumentsDialog";
import InvoiceCopyPanel from "./InvoiceCopyPanel";
import DocBundleCreator from "./DocBundleCreator";
import { ClipboardList, UserRound } from "lucide-react";
import StudentPicker from "./StudentPicker";

// V1.55 · Página "Crear documentos" — la administración genera aquí, en una
// sola pantalla y con los módulos ya abiertos, todos los documentos que el
// alumno debe firmar. Reutiliza al 100% los formularios y flujos existentes.

export default function CrearDocumentosTab() {
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [contracts, setContracts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/students");
        setStudents((data || []).filter((s) => s.active !== false));
      } catch (err) { console.warn("students:", err); }
    })();
  }, []);

  const student = students.find((s) => s.id === studentId) || null;

  useEffect(() => {
    if (!studentId) { setContracts([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/contracts", { params: { student_id: studentId } });
        setContracts(data || []);
      } catch (err) { console.warn("contracts:", err); }
    })();
  }, [studentId]);

  const reloadContracts = async () => {
    if (!studentId) return;
    try {
      const { data } = await api.get("/contracts", { params: { student_id: studentId } });
      setContracts(data || []);
    } catch { /* noop */ }
  };

  const editableContract = (contracts || [])
    .filter((c) => (c.document_kind || "contract") === "contract" && !c.frozen && !c.signed)
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0] || null;

  return (
    <div className="space-y-4 max-w-4xl" data-testid="crear-documentos-tab">
      <div>
        <h2 className="text-lg font-bold flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-orange-500" /> Crear documentos
        </h2>
        <p className="text-xs text-neutral-500">
          Genera aquí todos los documentos que el alumno debe firmar y envíaselos en un único enlace.
        </p>
      </div>

      {/* Alumno (contexto compartido de toda la página) */}
      <div className="card-surface p-4">
        <label className="text-xs font-semibold mb-1 block flex items-center gap-1.5">
          <UserRound className="h-4 w-4 text-neutral-500" /> Alumno *
        </label>
        {student ? (
          <div className="flex items-center justify-between rounded-lg border border-emerald-300 bg-emerald-50 px-3 h-10 text-sm" data-testid="cd-selected-student">
            <span className="font-semibold">
              {studentShortLabel(student)}
              <span className="text-neutral-400 font-normal ml-2 text-xs">{student.phone}</span>
            </span>
            <button type="button" className="text-xs text-neutral-500 hover:text-red-600" onClick={() => setStudentId("")}>Cambiar</button>
          </div>
        ) : (
          <StudentPicker
            value={studentId}
            onChange={setStudentId}
            students={students}
            testId="cd-student-search"
            optionTestIdPrefix="cd-student"
            placeholder="Buscar alumno por nombre o teléfono…"
            renderMeta={(s) => s.phone || ""}
            triggerClassName="h-10 rounded-lg"
          />
        )}
      </div>

      {/* Módulo 1 · Configuración de firma y caducidad */}
      <CollapsibleSection
        id="cd-firma-metodo"
        title="1 · Firma digital · Método"
        subtitle="Directa, Email, SMS o Email/SMS — aplica a contratos y DGT"
      >
        <SignatureMethodSettings />
      </CollapsibleSection>
      <CollapsibleSection
        id="cd-links-ttl"
        title="1 · Enlaces · Vida útil"
        subtitle="Caducidad de los enlaces firmados enviados por WhatsApp/email"
      >
        <LinkTtlSettings />
      </CollapsibleSection>

      {!student && (
        <div className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-6 text-center text-sm text-neutral-500" data-testid="cd-pick-hint">
          Selecciona un alumno arriba para abrir los módulos de documentos.
        </div>
      )}

      {student && (
        <>
          {/* Módulo 2 · Datos de contrato — si el alumno ya tiene contrato
              sin firmar, se carga para editar (evita duplicados) */}
          <CollapsibleSection id="cd-contrato" title="2 · Datos de contrato" subtitle="Pack, precio de venta, descuento e IVA automáticos, cuotas y consentimientos" defaultOpen>
            {editableContract ? (
              <div className="mb-3 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-900" data-testid="cd-contract-existing">
                Editando el contrato existente <strong>{editableContract.numero}</strong> (sin firmar) — no se creará uno nuevo.
              </div>
            ) : (
              <div className="mb-3 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-600" data-testid="cd-contract-new">
                El alumno no tiene contrato pendiente — se creará uno nuevo.
              </div>
            )}
            <ContractForm
              key={editableContract?.id || "nuevo"}
              studentId={student.id}
              contract={editableContract}
              onSaved={reloadContracts}
            />
          </CollapsibleSection>

          {/* Módulo 3 · Autorización DGT */}
          <CollapsibleSection id="cd-aut-dgt" title="3 · Nueva Autorización DGT" subtitle="Representación ante la Dirección General de Tráfico" defaultOpen>
            <AutorizacionDialog embedded kind="autorizacion_dgt" studentId={student.id} students={[student]} onCreated={reloadContracts} />
          </CollapsibleSection>

          {/* Módulo 4 · Autorización representante legal (menor) */}
          <CollapsibleSection id="cd-aut-menor" title="4 · Nueva Autorización representante legal (menor)" subtitle="Firma del tutor obligatoria; la del alumno, opcional" defaultOpen>
            <AutorizacionDialog embedded kind="autorizacion_menor" studentId={student.id} students={[student]} onCreated={reloadContracts} />
          </CollapsibleSection>

          {/* Módulo 5 · Documentación DGT — ALICIA */}
          <CollapsibleSection id="cd-alicia" title="5 · Documentación DGT — ALICIA" subtitle="Mod.04 · Mod.07 · Mod.12 con la Ficha Maestra del alumno" defaultOpen>
            <DgtDocumentsDialog embedded open student={student} onClose={() => {}} />
          </CollapsibleSection>

          {/* Módulo 6 · Ficha de facturación */}
          <CollapsibleSection id="cd-factura" title="6 · Facturación (copia/pega)" subtitle="Datos listos para pegar en el emisor de facturas" defaultOpen>
            <InvoiceCopyPanel student={student} contracts={contracts} defaultOpen />
          </CollapsibleSection>

          {/* Módulo final · Selector y envío por WhatsApp */}
          <CollapsibleSection id="cd-selector" title="7 · Enviar al alumno · Enlace único" subtitle="Selecciona qué debe firmar y envíalo por WhatsApp con Kai" defaultOpen>
            <DocBundleCreator student={student} />
          </CollapsibleSection>
        </>
      )}
    </div>
  );
}
